'use client';

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

type ThemeContextType = {
  theme: ThemePreference;
  isDarkMode: boolean;
  setTheme: (theme: ThemePreference) => void;
  toggleDarkMode: () => void;
};

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'trainme-theme';

const getSystemPreferredDarkMode = (): boolean => {
  if (typeof window === 'undefined') return true;

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
};

const getStoredTheme = (): ThemePreference | null => {
  if (typeof window === 'undefined') return null;

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (
    storedTheme === 'dark' ||
    storedTheme === 'light' ||
    storedTheme === 'system'
  ) {
    return storedTheme;
  }
  return null;
};

const ThemeContext = createContext<ThemeContextType>({
  // Default to dark mode during SSR and initial client render to avoid mismatch.
  // The actual theme will be determined and applied on the client in useEffect.
  theme: 'system',
  isDarkMode: true,
  setTheme: () => {},
  toggleDarkMode: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemePreference] = useState<ThemePreference>('system');
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);

  useEffect(() => {
    const storedTheme = getStoredTheme();
    if (storedTheme) setThemePreference(storedTheme);
  }, []);

  useEffect(() => {
    const updateEffectiveTheme = () => {
      setIsDarkMode(
        theme === 'dark' || (theme === 'system' && getSystemPreferredDarkMode())
      );
    };

    updateEffectiveTheme();

    if (theme !== 'system' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => updateEffectiveTheme();
    mediaQuery.addEventListener?.('change', handleChange);

    return () => mediaQuery.removeEventListener?.('change', handleChange);
  }, [theme]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const setTheme = (nextTheme: ThemePreference) => {
    setThemePreference(nextTheme);
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
  };

  const toggleDarkMode = () => {
    setTheme(isDarkMode ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider
      value={{ theme, isDarkMode, setTheme, toggleDarkMode }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
